import { isPlatform } from '@ionic/react';
import { transportTimeoutMs, withAbortTimeout } from '../helpers/transport-timeout.helpers';

export const isWeb = isPlatform('desktop') || isPlatform('mobileweb');
export const videoSrc = isWeb ? 'https://videos-for-scoring.storage.googleapis.com' : '/videos';

// HEAD-probe policy knobs. Overridable (e.g. 0ms delay) so tests exercise retries without waiting.
export const VIDEO_PROBE = { maxAttempts: 3, baseRetryMs: 500 };

// Persisted per session so a probe verdict survives navigation/reload; the My Assignments list and
// the scorer read the same cache via unavailableFilenamesFor and can't drift. See CLAUDE.md.
const SESSION_KEY = 'mea_video_availability';

const readPersistedCache = (): Map<string, boolean> => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? new Map(Object.entries(JSON.parse(raw) as { [filename: string]: boolean })) : new Map();
  } catch (err) {
    console.warn('video.utils: could not read persisted availability cache; starting empty', err);
    return new Map();
  }
};

// filename → reachable? Absent = never probed. Only DEFINITIVE verdicts are stored (see probeVideoFile).
const availabilityCache = readPersistedCache();
// Concurrent probes of the same file share one request instead of racing duplicate HEADs.
const inFlight = new Map<string, Promise<boolean>>();

const persistCache = (): void => {
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(Object.fromEntries(availabilityCache)));
  } catch (err) {
    console.warn('video.utils: could not persist availability cache; continuing in-memory only', err);
  }
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Definitively gone (drop at once) vs. access-denied (drop only once persistent). Everything
// else — 405/501 (HEAD unsupported), 5xx, network error — is indeterminate and kept on exhaustion.
const MISSING_STATUS = new Set([404, 410]);
const ACCESS_DENIED_STATUS = new Set([401, 403]);

// `definitive` distinguishes a settled verdict (200 / 404-410 / persistent 401-403) — safe to cache —
// from an indeterminate one (405/501/5xx/network) that is kept but must be re-probed, not cached.
interface IProbeResult { reachable: boolean; definitive: boolean; }

// One file's reachability + whether the verdict is definitive. Retries transient failures with
// backoff; an indeterminate result never hides a clip and is never cached. See CLAUDE.md.
const probeReachable = async (filename: string): Promise<IProbeResult> => {
  for (let attempt = 1; attempt <= VIDEO_PROBE.maxAttempts; attempt += 1) {
    const isLast = attempt >= VIDEO_PROBE.maxAttempts;
    try {
      const response = await withAbortTimeout(`video probe ${filename}`, transportTimeoutMs('default'), (signal) => (
        fetch(`${videoSrc}/${filename}`, { method: 'HEAD', signal })
      ));
      if (response.ok) return { reachable: true, definitive: true };
      if (MISSING_STATUS.has(response.status)) return { reachable: false, definitive: true };
      if (isLast) {
        return ACCESS_DENIED_STATUS.has(response.status)
          ? { reachable: false, definitive: true }
          : { reachable: true, definitive: false };
      }
    } catch (err) {
      if (isLast) {
        console.warn(`checkVideoFilesExist: HEAD ${filename} failed after ${attempt} attempts; keeping (indeterminate)`, err);
        return { reachable: true, definitive: false };
      }
    }
    await delay(VIDEO_PROBE.baseRetryMs * attempt);
  }
  return { reachable: true, definitive: false };
};

// Probe (or return cached) one file's reachability, deduping concurrent probes. Only a definitive
// verdict is cached and persisted; an indeterminate result is returned but re-probed next call.
const probeVideoFile = (filename: string): Promise<boolean> => {
  const cached = availabilityCache.get(filename);
  if (cached !== undefined) return Promise.resolve(cached);
  const existing = inFlight.get(filename);
  if (existing) return existing;
  const probe = probeReachable(filename).then(({ reachable, definitive }) => {
    if (definitive) { availabilityCache.set(filename, reachable); persistCache(); }
    inFlight.delete(filename);
    return reachable;
  });
  inFlight.set(filename, probe);
  return probe;
};

// Filenames confirmed reachable (parallel probes, cached). Batch entry point that warms the
// shared cache; callers derive exclusions from it (or read the cache via unavailableFilenamesFor).
export const checkVideoFilesExist = async (filenames: string[]): Promise<Set<string>> => {
  const unique = Array.from(new Set(filenames));
  const results = await Promise.all(unique.map(async (filename) => ({ filename, ok: await probeVideoFile(filename) })));
  return new Set(results.filter((result) => result.ok).map((result) => result.filename));
};

// Sync read: the subset of `filenames` KNOWN unreachable (cached false). Un-probed files are
// omitted (treated as available) so an un-probed clip still counts toward progress.
export const unavailableFilenamesFor = (filenames: string[]): Set<string> =>
  new Set(filenames.filter((filename) => availabilityCache.get(filename) === false));

// Record a directly-observed result (e.g. a <video> load error) so both surfaces exclude it.
export const setVideoAvailability = (filename: string, reachable: boolean): void => {
  availabilityCache.set(filename, reachable);
  persistCache();
};

// Test seam: drop all cached probe results so each test starts from an un-probed cache.
export const clearVideoAvailabilityCache = (): void => {
  availabilityCache.clear();
  inFlight.clear();
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch (err) {
    console.warn('video.utils: could not clear persisted availability cache', err);
  }
};
